import React, { useEffect, useRef, useState } from 'react';

import {
    defaultQuoteDataForm,
    QuoteDataForm,
    QuoteOfferTypeDataForm,
    QuoteReferenceDataForm
} from '../../../app/models/Quote';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import QuoteForm from './forms/QuoteForm';
import useQuoteService from '../../../app/services/hooks/useQuoteService';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import { PaymentCondition } from '../../../app/models/PaymentCondition';
import { Currency } from '../../../app/models/Currency';
import useSweetAlert from '../../../hooks/useSweetAlert';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useClientService from '../../../app/services/hooks/useClientService';
import { Sector } from '../../../app/models/Sector';
import { SectorBusinessRulesEnum } from '../../../app/shared/enums';
import useSectorService from '../../../app/services/hooks/useSectorService';
import { ClientContact } from '../../../app/models/ClientContact';
import useClientContactService from '../../../app/services/hooks/useClientContactService';
import useBusinessNameContactService from '../../../app/services/hooks/useBusinessNameContactService';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const QuoteCreate = ({ onSaved, onCancel, onError }: Props) => {
    const [societies, setSocieties] = useState<Society[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [clientContacts, setClientContatcs] = useState<ClientContact[]>([]);
    const [paymentConditions, setPaymentsConditions] = useState<PaymentCondition[]>([]);
    const [offerTypes, setOfferTypes] = useState<QuoteOfferTypeDataForm[]>([]);
    const [references, setReferences] = useState<QuoteReferenceDataForm[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentCondition[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [sectors, setSectors] = useState<Sector[]>([]);


    const { 
        fetchingStoreQuote, 
        storeQuote, 
        createQuote, 
        fetchingCreateQuote,
    } = useQuoteService();

    const {
        getClientsBySociety,
        fetchingGetClientBySociety
    } = useClientService()

    const {
        getSectorsByBusinessRulesEnum,
        fetchingGetSectorsByBusinessRulesEnum
    } = useSectorService()

    
    const {
        getBusinessNameContactByClient,
    } = useBusinessNameContactService()


    //formulario
    const [quote, setQuote] = useState<QuoteDataForm>(defaultQuoteDataForm);
    const [errorFields, setErrorFields] = useState<Record<string, unknown> | undefined>();
    const { navigationPage } = useNavigationPage();
    const societyIdSelected = useRef<number | undefined>(0);
    const clientIdSelected = useRef<number | undefined>(0);
    // const sectorBusinessRulesSelected = useRef<string | undefined>('');

    const sectorBusinessRuleSelected = useRef<SectorBusinessRulesEnum | undefined>(undefined);


    useEffect(() => {
        createQuote({
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                setPaymentMethods(response.data.payment_methods);
                setCurrencies(response.data.currencies);
                setReferences(response.data.references);
                setOfferTypes(response.data.offer_types);
                setSectors(response.data.sectors)

            }
        });
    }, []);

    useEffect(() => {
        const selectedSocietyId = quote.society_id;

        if (selectedSocietyId == null || selectedSocietyId === '') {
            societyIdSelected.current = undefined;
            return;
        }

        const normalizedSocietyId = Number(selectedSocietyId);

        if (normalizedSocietyId !== societyIdSelected.current) {
            getClientsBySociety(normalizedSocietyId, {
                onSuccess: (response: ServiceResponse) => {
                    societyIdSelected.current = normalizedSocietyId;
                    setClients(response.data.clients);
                    setQuote((currentQuote) => ({
                        ...currentQuote,
                        client_id: 0
                    }));
                },
                onError: () => {
                    setQuote((currentQuote) => ({
                        ...currentQuote,
                        society_id: Number(societyIdSelected.current)
                    }));
                }
            })
        }
        
    }, [quote.society_id]);

    
    useEffect(() => {

        if (quote.client_id == undefined) {
            clientIdSelected.current = undefined;
        }

        if (quote.client_id && quote.client_id != clientIdSelected.current) {
            
            getBusinessNameContactByClient(Number(quote.client_id), {
                onSuccess: (response: ServiceResponse) => {
                    clientIdSelected.current = Number(quote.client_id) ?? 0;
                 
                    setClientContatcs(response.data.client_contacts)
                    setQuote(
                        {
                            ...quote,
                            client_contact_id: 0
                        }
                    )
                },
                onError: (response: ServiceResponse) => {
                    setQuote({
                        ...quote,
                        client_id: Number(clientIdSelected.current)
                    });
                }
            })
        }
        
    }, [quote.client_id]);


    const store = () => {
        useSweetAlert().spinnerAlert('Guardando cotización...');
        storeQuote(quote, {
            onSuccess: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.success(response.message);
                navigationPage('/quotes/' + response.data.quote_id + '/edit');

                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.error(response.message);
                if (onError) onError();
            },
            onFieldError: (errorFields: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                setErrorFields(errorFields.data);
            }
        });
    };


    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingCreateQuote ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <QuoteForm
                        quoteForm={quote}
                        setQuoteForm={setQuote}
                        societies={societies}
                        clients={clients}
                        references={references}
                        paymentMethods={paymentMethods}
                        currencies={currencies}
                        sectors={sectors}
                        clientContacts={clientContacts}
                        errorFields={errorFields}
                        offerTypes={offerTypes}
                        isCreated={true}
                        loadingClient={fetchingGetClientBySociety}

                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStoreQuote} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreQuote} />
                </div>
            </div>
        </>
    );
};

export default QuoteCreate;
